import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  InputAdornment,
} from "@mui/material";
import { DraggablePaper } from "./DraggablePaper";
import { useDebouncedCallback } from "../utils/useDebouncedCallback";

// Memoize StrokeDialog to prevent unnecessary re-renders
const StrokeDialog = React.memo(({ open, onClose, canvas, selectedObject }) => {
  const [strokeWidth, setStrokeWidth] = useState(0);
  const [strokeColor, setStrokeColor] = useState("#000000");
  const [prevStrokeWidth, setPrevStrokeWidth] = useState(0);
  const [prevStrokeColor, setPrevStrokeColor] = useState("#000000");
  const [isDirty, setIsDirty] = useState(false);
  const [inputStrokeWidth, setInputStrokeWidth] = useState("");
  const selectedObjectRef = useRef(selectedObject);

  // Update ref when selectedObject changes
  useEffect(() => {
    selectedObjectRef.current = selectedObject;
    console.log("[StrokeDialog] selectedObject updated:", selectedObject);
  }, [selectedObject]);

  // Initialize stroke values when dialog opens or selected object changes
  useEffect(() => {
    if (selectedObject && open) {
      const currentStrokeWidth = Math.round(
        Number(selectedObject.get("strokeWidth")) || 0
      );
      const currentStrokeColor = selectedObject.get("stroke") || "#000000";
      setStrokeWidth(currentStrokeWidth);
      setStrokeColor(currentStrokeColor);
      setInputStrokeWidth(currentStrokeWidth.toString());
      setPrevStrokeWidth(currentStrokeWidth);
      setPrevStrokeColor(currentStrokeColor);
      setIsDirty(false);
      console.log(
        "[StrokeDialog] Initialized strokeWidth:",
        currentStrokeWidth,
        "strokeColor:",
        currentStrokeColor
      );
    }
  }, [selectedObject, open]);

  // Memoize update callbacks
  const updateStrokeWidthCallback = useCallback(
    (value) => {
      const roundedValue = Number(value);
      setStrokeWidth(roundedValue);
      setInputStrokeWidth(roundedValue.toString());
      if (selectedObjectRef.current && canvas) {
        canvas.setActiveObject(selectedObjectRef.current);
        selectedObjectRef.current.set({ strokeWidth: roundedValue });
        selectedObjectRef.current.setCoords();
        canvas.requestRenderAll();
        setIsDirty(true);
        console.log(
          "[StrokeDialog] Updated strokeWidth:",
          roundedValue,
          "Object:",
          selectedObjectRef.current
        );
      } else {
        console.warn("[StrokeDialog] Update failed:", {
          selectedObject: selectedObjectRef.current,
          canvas,
        });
      }
    },
    [canvas]
  );

  const updateStrokeColorCallback = useCallback(
    (value) => {
      setStrokeColor(value);
      if (selectedObjectRef.current && canvas) {
        canvas.setActiveObject(selectedObjectRef.current);
        selectedObjectRef.current.set({ stroke: value });
        selectedObjectRef.current.setCoords();
        canvas.requestRenderAll();
        setIsDirty(true);
        console.log(
          "[StrokeDialog] Updated strokeColor:",
          value,
          "Object:",
          selectedObjectRef.current
        );
      } else {
        console.warn("[StrokeDialog] Update failed:", {
          selectedObject: selectedObjectRef.current,
          canvas,
        });
      }
    },
    [canvas]
  );

  // Create debounced update functions
  const updateStrokeWidth = useDebouncedCallback(
    updateStrokeWidthCallback,
    100,
    canvas
  );
  const updateStrokeColor = useDebouncedCallback(
    updateStrokeColorCallback,
    100,
    canvas
  );

  // Handle dialog close (cancel)
  const handleCancel = useCallback(() => {
    if (isDirty && selectedObject) {
      selectedObject.set({
        strokeWidth: prevStrokeWidth,
        stroke: prevStrokeColor,
      });
      selectedObject.setCoords();
      canvas?.requestRenderAll();
      console.log(
        "[StrokeDialog] Canceled, reverted to strokeWidth:",
        prevStrokeWidth,
        "strokeColor:",
        prevStrokeColor
      );
    }
    onClose();
  }, [
    isDirty,
    selectedObject,
    prevStrokeWidth,
    prevStrokeColor,
    canvas,
    onClose,
  ]);

  // Handle apply stroke (save changes and add to history)
  const handleApply = useCallback(() => {
    updateStrokeWidth.flush();
    updateStrokeColor.flush();
    if (isDirty && canvas && selectedObject) {
      canvas.setActiveObject(selectedObject);
      selectedObject.set({ strokeWidth, stroke: strokeColor });
      selectedObject.setCoords();
      canvas.requestRenderAll();
      canvas.fire("object:modified", { target: selectedObject });
      console.log(
        "[StrokeDialog] Applied strokeWidth:",
        strokeWidth,
        "strokeColor:",
        strokeColor,
        "Fired object:modified for:",
        selectedObject
      );
    } else {
      console.log(
        "[StrokeDialog] Apply skipped: isDirty=",
        isDirty,
        "canvas=",
        !!canvas,
        "selectedObject=",
        !!selectedObject
      );
    }
    setIsDirty(false);
    onClose();
  }, [
    isDirty,
    canvas,
    selectedObject,
    strokeWidth,
    strokeColor,
    updateStrokeWidth,
    updateStrokeColor,
    onClose,
  ]);

  // Stop mouse events from propagating to allow input interaction
  const stopPropagation = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // Prevent backdrop click from closing the dialog
  const handleBackdropClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // Prevent keydown events (e.g., Escape key) from closing the dialog
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
    }
  }, []);

  // Handle input changes
  const handleStrokeWidthChange = useCallback((e) => {
    setInputStrokeWidth(e.target.value);
  }, []);

  const handleStrokeWidthBlur = useCallback(() => {
    const value = Number(inputStrokeWidth);
    if (!isNaN(value) && value >= 0) {
      updateStrokeWidth(value);
    } else {
      setInputStrokeWidth(strokeWidth.toString());
    }
  }, [inputStrokeWidth, strokeWidth, updateStrokeWidth]);

  const handleStrokeWidthStep = useCallback(
    (e) => {
      const value = Number(e.target.value);
      if (!isNaN(value) && value >= 0) {
        updateStrokeWidth(value);
      }
    },
    [updateStrokeWidth]
  );

  const handleStrokeColorChange = useCallback(
    (e) => {
      updateStrokeColor(e.target.value);
    },
    [updateStrokeColor]
  );

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        handleCancel();
      }}
      PaperComponent={DraggablePaper}
      aria-labelledby="draggable-dialog-title"
      BackdropProps={{
        style: { backgroundColor: "rgba(0, 0, 0, 0.2)" },
        onClick: handleBackdropClick,
      }}
      onKeyDown={handleKeyDown}
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: "#fff",
          boxShadow: "0px 3px 5px rgba(0, 0, 0, 0.2)",
        },
      }}
    >
      <DialogTitle
        id="draggable-dialog-title"
        sx={{
          color: "#ffffff",
          backgroundColor: "#0d2445",
          fontSize: "20px",
          display: "flex",
          alignItems: "center",
          cursor: "move",
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ marginRight: "8px" }}
        >
          <circle cx="4" cy="4" r="1.5" fill="#ffffff" />
          <circle cx="8" cy="4" r="1.5" fill="#ffffff" />
          <circle cx="4" cy="8" r="1.5" fill="#ffffff" />
          <circle cx="8" cy="8" r="1.5" fill="#ffffff" />
          <circle cx="4" cy="12" r="1.5" fill="#ffffff" />
          <circle cx="8" cy="12" r="1.5" fill="#ffffff" />
        </svg>
        Stroke Toolbar
      </DialogTitle>
      <DialogContent
        onMouseDown={stopPropagation}
        onMouseMove={stopPropagation}
        onMouseUp={stopPropagation}
        onMouseLeave={stopPropagation}
      >
        <Stack direction="column" sx={{ marginTop: 1 }}>
          <TextField
            type="number"
            id="canvasStrokeWidth"
            margin="dense"
            value={inputStrokeWidth}
            onChange={handleStrokeWidthChange}
            onBlur={handleStrokeWidthBlur}
            onInput={handleStrokeWidthStep}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: <InputAdornment position="end">px</InputAdornment>,
              sx: {
                bgcolor: "#fff",
                height: 36,
                width: 122,
                paddingTop: 1,
                marginRight: 1,
              },
              inputProps: { step: 1, min: 0 }, // Step 1px for fine control
            }}
            label="Stroke Width"
            sx={{
              "& .MuiInputLabel-root": {
                fontFamily: "Roboto",
                fontWeight: 500,
                marginTop: 1,
              },
            }}
            variant="outlined"
            inputProps={{ "aria-label": "Stroke width in pixels" }}
          />
          <TextField
            type="color"
            id="canvasStrokeColor"
            margin="dense"
            value={strokeColor}
            onChange={handleStrokeColorChange}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              sx: {
                bgcolor: "#fff",
                height: 36,
                width: 122,
                paddingTop: 1,
                marginRight: 1,
              },
            }}
            label="Stroke Color"
            sx={{
              "& .MuiInputLabel-root": {
                fontFamily: "Roboto",
                fontWeight: 500,
                marginTop: 1,
              },
            }}
            variant="outlined"
            inputProps={{ "aria-label": "Stroke color" }}
          />
        </Stack>
      </DialogContent>
      <DialogActions
        onMouseDown={stopPropagation}
        onMouseMove={stopPropagation}
        onMouseUp={stopPropagation}
        onMouseLeave={stopPropagation}
      >
        <Button
          onClick={handleCancel}
          color="secondary"
          aria-label="Cancel stroke changes"
        >
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          color="primary"
          aria-label="Apply stroke changes"
        >
          Apply Stroke
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default StrokeDialog;
